import { vetor, type Vetor3 } from '../geometria/vetor3'

/**
 * Catálogo de animais e objetos soltos no habitáculo.
 *
 * Existe pela mesma razão do ocupante sem cinto: o que não está preso ao
 * veículo continua com a velocidade que tinha quando o veículo muda de
 * velocidade. `retencaoOcupante.ts` já resolve a trajetória livre — aqui só
 * entra o que é próprio da carga solta: massa, dimensão e de onde ela parte.
 */
/**
 * Onde, dentro do veículo, a carga solta estava antes do impacto.
 *
 * Era um tipo anônimo dentro de `CorpoSolto` e ninguém o lia: toda carga nascia
 * na âncora do motorista, então a caixa do porta-malas partia do colo da vítima.
 * Nomeado e exportado porque agora a UI oferece a escolha e
 * `cargaSoltaColocacao.ts` converte cada valor em um ponto do habitáculo.
 */
export type OrigemCarga = 'bancoTraseiro' | 'portaMalas' | 'painel' | 'assoalho' | 'colo'

export interface CorpoSolto {
  id: string
  nome: string
  tipo: 'animal' | 'objeto'
  massaKg: number
  dimensoes: Vetor3
  origemPadrao: OrigemCarga
}

export const CORPOS_SOLTOS: Readonly<Record<string, CorpoSolto>> = {
  cachorroMedio: {
    id: 'cachorroMedio',
    nome: 'Cachorro de porte médio',
    tipo: 'animal',
    massaKg: 20,
    dimensoes: vetor(0.6, 0.5, 0.25),
    origemPadrao: 'bancoTraseiro',
  },
  gatoDomestico: {
    id: 'gatoDomestico',
    nome: 'Gato doméstico',
    tipo: 'animal',
    massaKg: 4,
    dimensoes: vetor(0.4, 0.25, 0.2),
    origemPadrao: 'colo',
  },
  mochila: {
    id: 'mochila',
    nome: 'Mochila',
    tipo: 'objeto',
    massaKg: 5,
    dimensoes: vetor(0.3, 0.4, 0.2),
    origemPadrao: 'bancoTraseiro',
  },
  extintor: {
    id: 'extintor',
    nome: 'Extintor de incêndio',
    tipo: 'objeto',
    massaKg: 2,
    dimensoes: vetor(0.15, 0.35, 0.15),
    origemPadrao: 'assoalho',
  },
  caixaFerramentas: {
    id: 'caixaFerramentas',
    nome: 'Caixa de ferramentas',
    tipo: 'objeto',
    massaKg: 12,
    dimensoes: vetor(0.4, 0.25, 0.25),
    origemPadrao: 'portaMalas',
  },
  notebook: {
    id: 'notebook',
    nome: 'Notebook',
    tipo: 'objeto',
    massaKg: 2,
    dimensoes: vetor(0.35, 0.03, 0.25),
    origemPadrao: 'painel',
  },
}

export const IDS_CORPO_SOLTO: readonly string[] = Object.keys(CORPOS_SOLTOS)

export function corpoSoltoPorId(id: string): CorpoSolto {
  return CORPOS_SOLTOS[id] ?? CORPOS_SOLTOS.mochila!
}

const GRAVIDADE = 9.81

/**
 * Distância de "parada" assumida para converter energia cinética em peso
 * equivalente: quanto o corpo afunda no que encontrou antes de parar.
 *
 * É aproximação didática — não medição de engenharia de colisão — para dar ao
 * aluno uma grandeza comparável (quilogramas-força) a partir de uma energia em
 * joules. Convenção pendente de confirmação do instrutor, como o limiar axial
 * de `anguloExtracao.ts`.
 */
export const DISTANCIA_PARADA_REFERENCIA_M = 0.3

export interface EnergiaImpacto {
  joules: number
  pesoEquivalenteKgf: number
}

/**
 * Energia cinética de uma massa na velocidade dada, e o peso que ela "parece"
 * ter ao impacto.
 *
 * Recebe massa, e não um `CorpoSolto`, porque o ocupante também é um corpo que
 * bate: a mesma conta serve para o cachorro de 20 kg do banco traseiro e para o
 * motorista de 78 kg sem cinto.
 */
export function energiaDeMassa(massaKg: number, velocidadeKmh: number): EnergiaImpacto {
  const velocidadeMs = velocidadeKmh / 3.6
  const joules = 0.5 * massaKg * velocidadeMs * velocidadeMs
  const pesoEquivalenteKgf = joules / (GRAVIDADE * DISTANCIA_PARADA_REFERENCIA_M)
  return { joules, pesoEquivalenteKgf }
}

/** Energia cinética do corpo solto na velocidade dada. */
export function energiaDeImpacto(corpo: CorpoSolto, velocidadeKmh: number): EnergiaImpacto {
  return energiaDeMassa(corpo.massaKg, velocidadeKmh)
}
